import React, { useState, useEffect, useRef } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import 'keen-slider/keen-slider.min.css';

const servicesData = [
  { id: 1, title: "AI & Automation", desc: "At Mobikode, we help forward-thinking businesses make the most of AI and intelligent automation.", gradient: "from-[#4D71EF] via-[#263475] to-[#0A1128]" },
  { id: 2, title: "ServiceNow Consulting", desc: "Streamline workflows, integrate legacy systems, and maximize operational capability with expert modules.", gradient: "from-[#A2B6C3] via-[#5F7182] to-[#1C242B]" },
  { id: 3, title: "Cloud Engineering", desc: "Migrate infrastructure smoothly and scale modern microservices efficiently across architectures.", gradient: "from-[#A7F3D0] via-[#34D399] to-[#064E3B]" },
  { id: 4, title: "Industrial Automation", desc: "Bridge operational systems with robust processing frameworks for real-time edge orchestration.", gradient: "from-[#BAE6FD] via-[#38BDF8] to-[#0C4A6E]" },
  { id: 5, title: "MES Solutions", desc: "Optimize manufacturing execution steps dynamically with integrated telemetry platforms.", gradient: "from-[#C7D2FE] via-[#6366F1] to-[#312E81]" },
  { id: 6, title: "Infor M3 Solutions", desc: "Configure enterprise resource management tracks safely to secure logistics pipelines.", gradient: "from-[#CBD5E1] via-[#64748B] to-[#0F172A]" },
  { id: 7, title: "Building Automation", desc: "Coordinate facilities controls perfectly under centralized low-latency automation networks.", gradient: "from-[#CCFBF1] via-[#14B8A6] to-[#115E59]" }
];

const OurServicesSection = () => {
  const [activeId, setActiveId] = useState(1);
  const [loaded, setLoaded] = useState(false);
  const autoPlayRef = useRef(null);

  const [sliderRef, instanceRef] = useKeenSlider({
    loop: true,
    initial: 0,
    slides: {
      perView: "auto",
      spacing: 24,
    },
    slideChanged(slider) {
      const relIdx = slider.track.details.rel;
      if (servicesData[relIdx]) {
        setActiveId(servicesData[relIdx].id);
      }
    },
    created(s) {
      setLoaded(true);
      startAutoplay(s);
    },
    destroyed() {
      stopAutoplay();
    }
  });

  const startAutoplay = (sliderInstance) => {
    stopAutoplay();
    autoPlayRef.current = setInterval(() => {
      if (sliderInstance && !sliderInstance.destroyed) {
        sliderInstance.next();
      }
    }, 4500);
  };

  const stopAutoplay = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
  };

  return (
    <section className="w-full bg-[#FFFFFF] pb-16 sm:pb-20 lg:pb-24 overflow-hidden select-none text-left relative z-10">
      <div className="max-w-[1440px] w-full mx-auto px-5 sm:px-10 lg:px-[80px]">

        <div className="w-full flex items-center justify-between gap-6 mb-16">
          <h2 className="text-[#0A2540] font-sora font-[600] text-[200x] lg:text-[30px] leading-[1.2] tracking-tight">
            Our Services
          </h2>

          {loaded && instanceRef.current && (
            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={() => instanceRef.current?.prev()}
                className="w-10 h-10 border border-[#ED7A3A] bg-transparent flex items-center justify-center text-[#ED7A3A] hover:bg-[#ED7A3A] hover:text-white transition-all duration-300 cursor-pointer rounded-full"
                aria-label="Previous service"
              >
                <ArrowLeft size={18} strokeWidth={2} />
              </button>
              <button
                onClick={() => instanceRef.current?.next()}
                className="w-10 h-10 border border-[#ED7A3A] bg-transparent flex items-center justify-center text-[#ED7A3A] hover:bg-[#ED7A3A] hover:text-white transition-all duration-300 cursor-pointer rounded-full"
                aria-label="Next service"
              >
                <ArrowRight size={18} strokeWidth={2} />
              </button>
            </div>
          )}
        </div>

        <div
          ref={sliderRef}
          className="keen-slider w-full !overflow-visible"
          onMouseEnter={stopAutoplay}
          onMouseLeave={() => instanceRef.current && startAutoplay(instanceRef.current)}
        >
          {servicesData.map((service) => {
            const isSelected = service.id === activeId;

            return (
              <div
                key={service.id}
                className="keen-slider__slide"
                onMouseEnter={() => setActiveId(service.id)}
                style={{
                  minWidth: isSelected ? "340px" : "120px",
                  maxWidth: isSelected ? "340px" : "120px",
                  transition: "all 500ms cubic-bezier(0.25, 1, 0.5, 1)"
                }}
              >
                <div
                  className={`w-full h-[440px] p-6 sm:p-8 flex flex-col relative overflow-hidden transition-all duration-500 ${isSelected
                    ? `bg-gradient-to-br ${service.gradient} justify-between items-start opacity-100`
                    : `bg-gradient-to-b ${service.gradient} justify-center items-center cursor-pointer`
                    }`}
                >
                  {isSelected ? (
                    <div className="w-full h-full flex flex-col justify-between items-start opacity-100 transition-opacity duration-300 delay-100">
                      <div className="w-full flex flex-col items-start gap-4">
                        <h3 className="text-white font-sora text-[16px] sm:text-[20px] font-[600] tracking-tight leading-snug">
                          {service.title}
                        </h3>
                        <p className="text-white/80 font-graphik text-[14px] sm:text-[16px] leading-relaxed font-[500] max-w-[280px]">
                          {service.desc}
                        </p>
                      </div>

                      <div className="w-full pt-4">
                        <a
                          href={`/services/${service.id}`}
                          className="text-white font-graphik text-[14px] lg:text-[16px] font-[500] tracking-wide flex items-center gap-1.5 border-b border-white/20 pb-0.5 w-max hover:border-white transition-all"
                        >
                          <span>Read More</span>
                          <span className="text-[11px]">➔</span>
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center opacity-100 transition-opacity duration-300">
                      <span className="text-[#01386A] font-sora text-[16px] sm:text-[18px] font-medium tracking-wide whitespace-nowrap rotate-180 [writing-mode:vertical-lr] text-center">
                        {service.title}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default OurServicesSection;